import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaBoxOpen,
  FaClipboardList,
  FaCreditCard,
  FaFileArchive,
  FaImages,
  FaMapMarkerAlt,
  FaShippingFast,
  FaUser,
  FaWhatsapp,
} from "react-icons/fa";
import { getOrderDetails } from "../../api/orders";
import { Card, CardBody, CardHeader } from "../../common/Card";
import { Badge } from "../../common/Feedback";
import RecordActions from "../Shared/RecordActions";
import FullPageLoader from "../../common/FullPageLoader";

const getStatusTone = (status) => {
  const normalizedStatus = String(status || "").toUpperCase();

  if (
    ["PAID", "CAPTURED", "COMPLETED", "DELIVERED", "SUCCESS", "CONFIRMED"].some((value) =>
      normalizedStatus.includes(value),
    )
  ) {
    return "success";
  }

  if (["PENDING", "PROCESSING", "AWAITING"].some((value) => normalizedStatus.includes(value))) {
    return "warning";
  }

  if (
    ["SHIPMENT_CREATED", "SHIPPED", "IN_TRANSIT", "DISPATCHED", "PICKED_UP"].some((value) =>
      normalizedStatus.includes(value),
    )
  ) {
    return "info";
  }

  if (
    ["FAILED", "CANCELLED", "CANCELED", "REFUNDED", "REJECTED", "RTO"].some((value) =>
      normalizedStatus.includes(value),
    )
  ) {
    return "danger";
  }

  return "neutral";
};

const StatusValue = ({ value }) => <Badge tone={getStatusTone(value)}>{value || "N/A"}</Badge>;

const DetailRow = ({ label, children, className = "" }) => (
  <div className={`np-order-detail-row ${className}`.trim()}>
    <span className="np-order-detail-label">{label} :</span>
    <span className="np-order-detail-value">{children}</span>
  </div>
);

const OrderSection = ({ title, icon: Icon, children, className = "" }) => (
  <Card className={`np-order-section ${className}`.trim()}>
    <CardHeader className="np-order-section__header">
      <span className="np-order-section__icon" aria-hidden="true">
        <Icon />
      </span>
      <h2 className="np-order-section__title">{title}</h2>
    </CardHeader>
    <CardBody className="np-order-section__body">{children}</CardBody>
  </Card>
);

const downloadFileToDevice = async (fileUrl, fallbackName) => {
  try {
    const response = await fetch(fileUrl);
    if (!response.ok) throw new Error("Download failed");

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const urlFileName = new URL(fileUrl, window.location.href).pathname.split("/").pop();
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = decodeURIComponent(urlFileName || fallbackName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("File download error:", error);
    alert("Unable to download file. Please try again.");
  }
};

export default function OrderView() {
  const location = useLocation();
  const navigate = useNavigate();
  const id = location?.state?.orderId;

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";

    const date = new Date(dateStr);

    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();

    return `${dd}/${mm}/${yyyy}`;
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await getOrderDetails(id);

        setOrder(res?.data || res);
      } catch (err) {
        console.error(err);
        setOrder(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      load();
    } else {
      setLoading(false);
    }
  }, [id]);

  if (loading) return <FullPageLoader text="Loading order..." />;
  if (!order) return <div className="container-fluid p-4"><h5>Order not found</h5></div>;

  return (
    <div className="container-fluid p-4 np-order-view-page">
      <RecordActions onBack={() => navigate("/orders-list")} />
      <div className="np-order-detail-grid">
        <div className="np-order-detail-column">
          <OrderSection title="Customer Information" icon={FaUser}>
            <DetailRow label="Customer Name">{order.address?.fullName || "N/A"}</DetailRow>
            <DetailRow label="Mobile">{order.address?.phone || "N/A"}</DetailRow>
            <DetailRow label="City">{order.address?.city || "N/A"}</DetailRow>
            <DetailRow label="Pincode">{order.address?.pincode || "N/A"}</DetailRow>
          </OrderSection>

          <OrderSection title="Delivery Address" icon={FaMapMarkerAlt}>
            <DetailRow label="Address">
              {order.address?.address || order.address?.addressLine1 || ""}
              {order.address?.addressLine2 ? `, ${order.address.addressLine2}` : ""}
            </DetailRow>
            <DetailRow label="City">{order.address?.city || "N/A"}</DetailRow>
            <DetailRow label="State">{order.address?.state || "N/A"}</DetailRow>
            <DetailRow label="Pincode">{order.address?.pincode || "N/A"}</DetailRow>
          </OrderSection>

          <OrderSection title="Product Details" icon={FaBoxOpen}>
            {order.items?.map((item, index) => (
              <div
                key={index}
                className={`np-order-product ${index !== order.items.length - 1 ? "np-order-product--divided" : ""}`.trim()}
              >
                <DetailRow label="Product">{item.name}</DetailRow>
                <DetailRow label="Variation">{item.selections?.variantName || "N/A"}</DetailRow>
                <DetailRow label="Quantity">{item.quantity}</DetailRow>
              </div>
            ))}
          </OrderSection>
        </div>

        <div className="np-order-detail-column">
          <OrderSection title="Order Information" icon={FaClipboardList}>
            <DetailRow label="Order ID">{order.orderId || "N/A"}</DetailRow>
            <DetailRow label="Order Date">{formatDate(order.createdAt)}</DetailRow>
            <DetailRow label="Status">
              <StatusValue value={order.status} />
            </DetailRow>
            <DetailRow label="Payment">
              <StatusValue value={order.payment?.paymentStatus} />
            </DetailRow>
          </OrderSection>

          <OrderSection title="Payment Summary" icon={FaCreditCard}>
            <DetailRow label="Subtotal">₹{order.subtotal || 0}</DetailRow>
            <DetailRow label="Tax">₹{order.tax?.totalTax || 0}</DetailRow>
            <DetailRow label="Shipping">₹{order.shipment?.shippingCharge || 0}</DetailRow>
            <DetailRow label="Payment Method">{order.payment?.method || "N/A"}</DetailRow>
            <DetailRow label="Payment Status">
              <StatusValue value={order.payment?.paymentStatus} />
            </DetailRow>
            <div className="np-order-total">
              <span className="np-order-total__label">Total Amount :</span>
              <span className="np-order-total__value">₹{order.total || 0}</span>
            </div>
          </OrderSection>

          <OrderSection title="Shipping Details" icon={FaShippingFast}>
            <DetailRow label="Dispatch Date">{formatDate(order.shipment?.dispatch_date)}</DetailRow>
            <DetailRow label="Courier">{order.shipment?.courierName || "N/A"}</DetailRow>
            <DetailRow label="Shipment Status">
              <StatusValue value={order.shipment?.shipmentStatus} />
            </DetailRow>
            <DetailRow label="Delivery Days">{order.shipment?.deliveryDays || "N/A"}</DetailRow>
          </OrderSection>
        </div>
      </div>
      {/* ================= IMAGE DETAILS ================= */}
      <div className="row g-4 mt-1">
        <div className="col-12">
          <h5 className="mb-0">Verification Files</h5>
        </div>

        {/* Design Image */}
        <div className="col-lg-4 col-md-6 col-12">
          <OrderSection title="Design File" icon={FaFileArchive}>
              {order?.designFileUrl ? (
                <div
                  className="d-flex flex-column align-items-center justify-content-center rounded border"
                  style={{
                    height: "250px",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  <div
                    className="d-flex align-items-center justify-content-center rounded mb-3"
                    style={{
                      width: "70px",
                      height: "70px",
                      backgroundColor: "#dc3545",
                      color: "#fff",
                      fontSize: "30px",
                      fontWeight: "bold",
                    }}
                  >
                    ZIP
                  </div>

                  <h6 className="mb-1">Design ZIP File</h6>

                  <p className="text-muted small mb-3">
                    Download the uploaded design files
                  </p>

                  <a
                    href={order.designFileUrl}
                    className="btn btn-primary btn-sm"
                    download
                  >
                    Download ZIP
                  </a>
                </div>
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center rounded"
                  style={{
                    height: "250px",
                    backgroundColor: "#f8f9fa",
                    color: "#6c757d",
                  }}
                >
                  No design file available
                </div>
              )}
          </OrderSection>
        </div>

        {/* Final Design Proof */}
        <div className="col-lg-4 col-md-6 col-12">
          <OrderSection title="Final Design Proof" icon={FaImages}>
              {order?.finalDesignProofUrl?.length > 0 ? (
                <div className="row g-2">
                  {order.finalDesignProofUrl.map((file, index) => {
                    const fileUrl = file?.url || "";
                    const cleanUrl = fileUrl.split("?")[0].toLowerCase();

                    const isPdf = cleanUrl.endsWith(".pdf");

                    return (
                      <div className="col-6" key={index}>
                        {fileUrl ? (
                          isPdf ? (
                            /* ================= PDF ================= */
                            <div
                              className="d-flex flex-column align-items-center justify-content-center rounded border"
                              style={{
                                height: "120px",
                                backgroundColor: "#f8f9fa",
                              }}
                            >
                              <div
                                className="fw-bold text-danger mb-2"
                                style={{ fontSize: "22px" }}
                              >
                                PDF
                              </div>

                              <a
                                href={fileUrl}
                                className="btn btn-primary btn-sm"
                                onClick={(event) => {
                                  event.preventDefault();
                                  downloadFileToDevice(fileUrl, `design-proof-${index + 1}.pdf`);
                                }}
                              >
                                Download PDF
                              </a>
                            </div>
                          ) : (
                            /* ================= IMAGE ================= */
                            <img
                              src={fileUrl}
                              alt={`Final Design Proof ${index + 1}`}
                              className="img-fluid rounded border w-100"
                              style={{
                                height: "120px",
                                objectFit: "contain",
                                backgroundColor: "#f8f9fa",
                              }}
                            />
                          )
                        ) : (
                          /* ================= NO FILE ================= */
                          <div
                            className="d-flex align-items-center justify-content-center rounded"
                            style={{
                              height: "120px",
                              backgroundColor: "#f8f9fa",
                              color: "#6c757d",
                              fontSize: "13px",
                            }}
                          >
                            File unavailable
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center rounded"
                  style={{
                    height: "250px",
                    backgroundColor: "#f8f9fa",
                    color: "#6c757d",
                  }}
                >
                  No final design proof available
                </div>
              )}
          </OrderSection>
        </div>

        {/* WhatsApp Screenshot */}
        <div className="col-lg-4 col-md-6 col-12">
          <OrderSection title="WhatsApp Screenshot" icon={FaWhatsapp}>
              {order?.whatsappScreenshotUrl ? (
                <img
                  src={order.whatsappScreenshotUrl}
                  alt="WhatsApp Screenshot"
                  className="img-fluid rounded w-100"
                  style={{
                    height: "250px",
                    objectFit: "contain",
                    backgroundColor: "#f8f9fa",
                  }}
                />
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center rounded"
                  style={{
                    height: "250px",
                    backgroundColor: "#f8f9fa",
                    color: "#6c757d",
                  }}
                >
                  No WhatsApp screenshot available
                </div>
              )}
          </OrderSection>
        </div>
      </div>
    </div>
  );
}
